namespace $ {

	export type $shm_tests_release_project = {
		id: string
		title: string
		description: string
		repository: string
		path: string
		owners: readonly string[]
	}

	export type $shm_tests_release_change = {
		type: 'feature' | 'fix' | 'breaking' | 'docs' | 'internal'
		title: string
		issue?: string
	}

	export type $shm_tests_release_entry = {
		id: string
		project: string
		version: string
		channel: 'prerelease' | 'release'
		status: 'active' | 'superseded' | 'promoted' | 'published'
		created_at: string
		promoted_from?: string
		source: {
			commit: string
			branch: string
			dirty: boolean
		}
		notes: string
		changes: readonly $shm_tests_release_change[]
	}

	export type $shm_tests_release_registry = {
		schema_version: number
		updated_at: string | null
		projects: readonly $shm_tests_release_project[]
		releases: readonly $shm_tests_release_entry[]
	}

}

namespace $.$$ {

	export class $shm_tests_release extends $.$shm_tests_release {

		registry_uri() {
			return '/shm/tests/release/index.json'
		}

		@ $mol_mem
		registry() {
			return this.$.$mol_fetch.json( this.registry_uri(), { cache: 'no-store' } ) as $shm_tests_release_registry
		}

		projects() {
			return this.registry().projects
		}

		override spread_ids() {
			return this.projects().map( project => project.id )
		}

		override Spread( id: string ) {
			return this.Project( id )
		}

		override Spread_default() {
			const id = this.spread_ids()[ 0 ]
			return id ? this.Project( id ) : this.Empty()
		}

		project( id: string ) {
			return this.projects().find( project => project.id === id ) ?? null
		}

		override spread_title( id: string ) {
			return this.project_title( id )
		}

		project_title( id: string ) {
			return this.project( id )?.title ?? id
		}

		project_pages( id: string ) {
			return [ this.Overview( id ), this.History( id ) ]
		}

		project_info( id: string ) {
			const project = this.project( id )
			if( !project ) return ''
			return [
				project.description,
				`Репозиторий: ${ project.repository }`,
				`Путь: ${ project.path }`,
				`Ответственные: ${ project.owners.length ? project.owners.join( ', ' ) : 'не назначены' }`,
			].join( '\n\n' )
		}

		project_releases( id: string ) {
			return this.registry().releases
				.filter( release => release.project === id )
				.toSorted( ( left, right )=> right.created_at.localeCompare( left.created_at ) )
		}

		current_prerelease( id: string ) {
			return this.project_releases( id ).find( release => release.channel === 'prerelease' && release.status === 'active' ) ?? null
		}

		current_release( id: string ) {
			return this.project_releases( id ).find( release => release.channel === 'release' && release.status === 'published' ) ?? null
		}

		prerelease_title( id: string ) {
			return `Пререлиз: ${ this.current_prerelease( id )?.version ?? 'не создан' }`
		}

		stable_title( id: string ) {
			return `Релиз: ${ this.current_release( id )?.version ?? 'не создан' }`
		}

		release_rows( id: string ) {
			const releases = this.project_releases( id )
			return releases.length ? releases.map( release => this.Release( release.id ) ) : [ this.Empty() ]
		}

		release( id: string ) {
			return this.registry().releases.find( release => release.id === id ) ?? null
		}

		release_title( id: string ) {
			const release = this.release( id )
			if( !release ) return id
			return `${ release.channel === 'release' ? 'Релиз' : 'Пререлиз' } ${ release.version }`
		}

		release_meta( id: string ) {
			const release = this.release( id )
			if( !release ) return ''
			const source = release.source.commit ? release.source.commit.slice( 0, 8 ) : 'commit не указан'
			return `${ release.status } · ${ new Date( release.created_at ).toLocaleString( 'ru-RU' ) } · ${ source }`
		}

		release_notes( id: string ) {
			const release = this.release( id )
			if( !release ) return ''
			const changes = release.changes.map( change => `- [${ change.type }] ${ change.title }${ change.issue ? ` (${ change.issue })` : '' }` )
			return [ release.notes, ... changes ].filter( Boolean ).join( '\n' )
		}

	}

}
